import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/free-solid-svg-icons';

// Animações
const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(1); opacity: 0.8; }
`;

const dataFlow = keyframes`
  0% { background-position: 0% 0%; }
  100% { background-position: 200% 0%; }
`;

const glowPulse = keyframes`
  0% { box-shadow: 0 0 5px rgba(64, 128, 255, 0.2); }
  50% { box-shadow: 0 0 20px rgba(64, 128, 255, 0.5); }
  100% { box-shadow: 0 0 5px rgba(64, 128, 255, 0.2); }
`;

// Componentes estilizados
const ThumbnailContainer = styled.div`
  position: relative;
  width: 70%;
  max-width: 700px;
  margin: 1rem auto;
  aspect-ratio: 16/9;
  border-radius: 12px;
  overflow: hidden;
  background: linear-gradient(135deg, #111, #222);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 2px solid ${props => props.color || 'rgba(64, 128, 255, 0.5)'};
  animation: ${glowPulse} 4s infinite;
  
  @media (max-width: 768px) {
    width: 85%;
  }
`;

const ThumbnailOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    linear-gradient(45deg, rgba(0, 0, 0, 0.2) 25%, transparent 25%) 0 0,
    linear-gradient(-45deg, rgba(0, 0, 0, 0.2) 25%, transparent 25%) 0 0,
    linear-gradient(45deg, transparent 75%, rgba(0, 0, 0, 0.2) 75%) 0 0,
    linear-gradient(-45deg, transparent 75%, rgba(0, 0, 0, 0.2) 75%) 0 0;
  background-size: 20px 20px;
  z-index: 1;
`;

const DataLines = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, 
    transparent 0%, 
    ${props => props.color || 'rgba(64, 128, 255, 0.1)'} 20%, 
    transparent 40%,
    transparent 60%,
    ${props => props.color || 'rgba(64, 128, 255, 0.1)'} 80%,
    transparent 100%
  );
  background-size: 200% 100%;
  animation: ${dataFlow} 10s linear infinite;
  z-index: 2;
  opacity: 0.3;
`;

const PlatformLogo = styled.img`
  width: ${props => (props.platformId === 'twilio' || props.platformId === 'openai') ? '60px' : '80px'};
  height: ${props => (props.platformId === 'twilio' || props.platformId === 'openai') ? '60px' : '80px'};
  object-fit: contain;
  position: absolute;
  top: 25%;
  left: 50.25%;
  transform: translate(-50%, -50%);
  z-index: 3;
  filter: drop-shadow(0 0 10px ${props => props.color || 'rgba(64, 128, 255, 0.7)'});
  
  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
    top: 25%;
  }
`;

const PlayButtonContainer = styled.div`
  position: absolute;
  top: 45%;
  left: 43.3%;
  transform: translate(-50%, -50%);
  z-index: 3;
  animation: ${pulse} 2s infinite ease-in-out;
  
  @media (max-width: 768px) {
    top: 45%;
    left: 42.5%;
  }
`;

const PlayButton = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${props => props.color || 'rgba(64, 128, 255, 0.7)'};
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 32px;
  box-shadow: 0 0 30px ${props => props.color || 'rgba(64, 128, 255, 0.7)'};
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 0 40px ${props => props.color || 'rgba(64, 128, 255, 0.9)'};
  }
  
  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
    font-size: 16px;
  }
`;

const CornerDecoration = styled.div`
  position: absolute;
  width: 40px;
  height: 40px;
  border-style: solid;
  border-color: ${props => props.color || 'rgba(64, 128, 255, 0.7)'};
  border-width: 0;
  z-index: 3;
  
  &.top-left {
    top: 15px;
    left: 15px;
    border-top-width: 3px;
    border-left-width: 3px;
    border-top-left-radius: 8px;
  }
  
  &.top-right {
    top: 15px;
    right: 15px;
    border-top-width: 3px;
    border-right-width: 3px;
    border-top-right-radius: 8px;
  }
  
  &.bottom-left {
    bottom: 15px;
    left: 15px;
    border-bottom-width: 3px;
    border-left-width: 3px;
    border-bottom-left-radius: 8px;
  }
  
  &.bottom-right {
    bottom: 15px;
    right: 15px;
    border-bottom-width: 3px;
    border-right-width: 3px;
    border-bottom-right-radius: 8px;
  }
`;

const VideoThumbnail = ({ platformId, platformName, platformColor, logoSrc }) => {
  return (
    <ThumbnailContainer color={platformColor}>
      <ThumbnailOverlay />
      <DataLines color={platformColor} />
      
      <CornerDecoration className="top-left" color={platformColor} />
      <CornerDecoration className="top-right" color={platformColor} />
      <CornerDecoration className="bottom-left" color={platformColor} />
      <CornerDecoration className="bottom-right" color={platformColor} />
      
      <PlatformLogo 
        src={logoSrc} 
        alt={`${platformName} Logo`} 
        color={platformColor}
        platformId={platformId}
      />
      
      <PlayButtonContainer>
        <PlayButton color={platformColor}>
          <FontAwesomeIcon icon={faPlay} />
        </PlayButton>
      </PlayButtonContainer>
    </ThumbnailContainer>
  );
};

export default VideoThumbnail;
